import React,{useContext,useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {AppContext} from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMapPin,FiClock,FiCalendar,FiFileText,FiStar,FiChevronDown,FiChevronUp}=FiIcons;

const stripHtml=(html)=> html ? html.replace(/<[^>]*>?/gm,'') : '';

const DayAccordion=({day,activities,teams})=> {
  const [isOpen,setIsOpen]=useState(true);
  const {formatDateDDMMYYYY}=useContext(AppContext);

  const isOddDay=Number(day) % 2 !==0;
  const dayColorBg=isOddDay ? 'bg-nba-red' : 'bg-nba-blue';
  const iconColorClass=isOddDay ? 'text-nba-red' : 'text-nba-blue';
  const dayColorTextHover=isOddDay ? 'group-hover:text-nba-red' : 'group-hover:text-nba-blue';
  const headerHoverBg=isOddDay ? 'group-hover:bg-nba-red' : 'group-hover:bg-nba-blue';
  const headerLineHoverBg=isOddDay ? 'group-hover:bg-nba-red/50' : 'group-hover:bg-nba-blue/50';

  return (
    <div className="space-y-6">
      {/* Intestazione Accordion Clickable */}
      <button 
        onClick={()=> setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-6 cursor-pointer group outline-none"
      >
        <div className="flex items-center gap-6 flex-1 text-left">
          <div className={`bg-white text-black px-8 py-3 font-league text-4xl italic shadow-lg ${headerHoverBg} group-hover:text-white transition-colors`}>
            GIORNO {day}
          </div>
          <div className={`h-px flex-grow bg-white/10 ${headerLineHoverBg} transition-colors`} />
        </div>
        <SafeIcon icon={isOpen ? FiChevronUp : FiChevronDown} className="text-3xl text-white/40 group-hover:text-white transition-colors shrink-0" />
      </button>

      {/* Contenuto Animato */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{height: 0,opacity: 0}}
            animate={{height: 'auto',opacity: 1}}
            exit={{height: 0,opacity: 0}}
            transition={{duration: 0.4,ease: "easeInOut"}}
            className="overflow-hidden"
          >
            <div className="grid gap-12 pt-4 pb-12">
              {activities.map((act)=> (
                <div key={act.id} className="relative group">
                  <div className={`absolute -left-4 top-0 bottom-0 w-1 ${dayColorBg} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    <div className="lg:col-span-3 space-y-4">
                      <div className="font-league text-5xl italic text-white leading-none">{act.time}</div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-white/50 text-[10px] font-black uppercase tracking-widest">
                          <SafeIcon icon={FiCalendar} className={iconColorClass} /> {formatDateDDMMYYYY(act.event_date)}
                        </div>
                        {act.address && (
                          <div className="flex items-start gap-2 text-white/50 text-[10px] font-black uppercase tracking-widest leading-tight">
                            <SafeIcon icon={FiMapPin} className={`${iconColorClass} mt-0.5 shrink-0`} /> {act.address}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="lg:col-span-6 space-y-4">
                      <h3 className={`font-league text-5xl italic tracking-tight uppercase text-white leading-none ${dayColorTextHover} transition-colors`}>
                        {act.title}
                      </h3>
                      {act.notes && (
                        <div className="flex items-start gap-3">
                          <SafeIcon icon={FiFileText} className="text-white/20 mt-1 shrink-0" />
                          <p className="text-white/60 text-lg leading-relaxed italic">{act.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="lg:col-span-3 flex flex-col justify-center items-end border-l border-white/5 pl-8">
                      {act.counts_towards_score !==false && (
                        <>
                          <div className={`text-[10px] font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2 ${act.status==='live' ? 'text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]' : 'text-white/20'}`}>
                            {act.status==='live' ? (
                              <>
                                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span> LIVE SCORE
                              </>
                            ) : act.status==='final' ? (
                              <>
                                <SafeIcon icon={FiStar} /> FINAL SCORE
                              </>
                            ) : (
                              <> IN ARRIVO </>
                            )}
                          </div>
                          
                          <div className="flex gap-4 select-none cursor-default">
                            <div className="text-center">
                              <div className="text-[9px] font-bold text-nba-red uppercase mb-1 truncate max-w-[90px] mx-auto pointer-events-none" title={stripHtml(teams[0]?.name)}>
                                {stripHtml(teams[0]?.name) || 'TEAM 1'}
                              </div>
                              <div className="font-league text-5xl text-white pointer-events-none">{act.scoreT1}</div>
                            </div>
                            <div className="font-league text-3xl text-white/10 flex items-center pt-4 pointer-events-none">VS</div>
                            <div className="text-center">
                              <div className="text-[9px] font-bold text-nba-blue uppercase mb-1 truncate max-w-[90px] mx-auto pointer-events-none" title={stripHtml(teams[1]?.name)}>
                                {stripHtml(teams[1]?.name) || 'TEAM 2'}
                              </div>
                              <div className="font-league text-5xl text-white pointer-events-none">{act.scoreT2}</div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Schedule=()=> {
  const {schedule,textStyles,teams}=useContext(AppContext);
  const days=Object.keys(schedule).sort((a,b)=> Number(a) - Number(b));

  if (days.length===0) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-white/20 font-league text-4xl uppercase italic">
        Ancora nessun evento programmato.
      </div>
    );
  }

  return (
    <div className="py-12 space-y-20 max-w-6xl mx-auto px-4">
      <div className="border-b-4 border-white pb-8">
        <h1 className="font-league italic leading-none tracking-tighter uppercase dyn-scheduleTitle">
          {textStyles.scheduleTitle?.text}
        </h1>
      </div>

      <div className="space-y-4">
        {days.map((day)=> (
          <DayAccordion key={day} day={day} activities={schedule[day]} teams={teams} />
        ))}
      </div>
    </div>
  );
};

export default Schedule;